import { Box, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import type { SlotListColumnLayout } from './slot-list-layout';

/** Inset for icon buttons in expanded action rows (matches delete column). */
export const slotListExpandedColumnPaddingSx = {
  p: 1,
  boxSizing: 'border-box',
} as const;

export function slotListExpandedRightColumnSx(layout: SlotListColumnLayout): SxProps<Theme> {
  return {
    ...slotListExpandedColumnPaddingSx,
    minWidth: `${layout.rightWidthPx}px`,
    width: `${layout.rightWidthPx}px`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

type SlotListExpandedLeftColumnProps = {
  layout: SlotListColumnLayout;
  children?: ReactNode;
  sx?: SxProps<Theme>;
};

/**
 * Expanded action row left column — same total width as the main slot row left block.
 * Children fill the full column; an optional leading gutter is layout-only (aligns with drag/lock above).
 */
export function SlotListExpandedLeftColumn({ layout, children, sx }: SlotListExpandedLeftColumnProps) {
  return (
    <Box
      sx={{
        position: 'relative',
        boxSizing: 'border-box',
        minWidth: `${layout.leftMinWidthPx}px`,
        width: `${layout.leftMinWidthPx}px`,
        maxWidth: `${layout.leftMinWidthPx}px`,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {layout.leadingGutterPx > 0 ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: layout.leadingGutterPx,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...slotListExpandedColumnPaddingSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
